/**
 * System configuration.
 *
 * Every rate here is a value with an effective date, never a constant in code. Nicaraguan
 * tax and social-security rates move by reform, and re-running an old payroll or an old
 * margin report has to use the rates that were in force at the time — not today's.
 */

import { z } from 'zod';
import { FX_OFICIAL_2026, FX_SCALE, type FxRate } from './money.js';
import { cuentaBancoCordobasSchema, cuentasBancoSchema } from './bancos.js';
import { gastoFijoSchema } from './gastosFijos.js';

/**
 * No rate past a thousand córdobas a dollar. «Configuración» asks for one between 10 and 100; past
 * what any dollar could cost, every dollar the books turn into córdobas runs out of range, and every
 * sale in dollars is refused after it (2026-10-13).
 */
const TASA_TOPE = 1_000 * FX_SCALE;

/** Annual IR bracket for rentas del trabajo, in centavos. */
export const irBracketSchema = z.object({
  /** Lower bound of the bracket, inclusive, in centavos of annual taxable income. */
  desde: z.number().int().nonnegative(),
  /** Fixed amount owed at this bracket, in centavos. */
  base: z.number().int().nonnegative(),
  /** Marginal rate in basis points. 1500 = 15%. */
  tasaBp: z.number().int().nonnegative(),
});
export type IrBracket = z.infer<typeof irBracketSchema>;

/** DGI progressive table for rentas del trabajo, confirmed current for 2026. */
export const IR_TRAMOS_2026: IrBracket[] = [
  { desde: 0, base: 0, tasaBp: 0 },
  { desde: 10_000_000, base: 0, tasaBp: 1_500 },
  { desde: 20_000_000, base: 1_500_000, tasaBp: 2_000 },
  { desde: 35_000_000, base: 4_500_000, tasaBp: 2_500 },
  { desde: 50_000_000, base: 8_250_000, tasaBp: 3_000 },
];

export const settingsSchema = z.object({
  negocioNombre: z.string().min(1).default('Abastece Ya'),
  /**
   * What the shop says about itself on a receipt, under its name. Each one is optional and left
   * off the receipt when empty: most of Nicaragua has no street addresses («del parque, 1c al
   * sur» is one), and plenty of pulperías have no RUC to print.
   */
  negocioDireccion: z.string().max(120).default(''),
  negocioTelefono: z.string().max(40).default(''),
  negocioRuc: z.string().max(20).default(''),
  /** The receipt's last line. Empty leaves it off. */
  reciboPie: z.string().max(120).default('¡Gracias por su compra!'),

  // --- Divisas ---
  /**
   * C$ per USD × 10,000. What the counter actually pays for a dollar.
   *
   * Branded on the way out so a rate read from config can be handed straight to the currency
   * conversion helpers. Without the brand every call site would have to re-validate, and one
   * of them would eventually forget.
   */
  fxTasaTienda: z
    .number()
    .int()
    .positive()
    .max(TASA_TOPE)
    .default(FX_OFICIAL_2026)
    .transform((n) => n as FxRate),
  /** BCN reference rate. The gap against the store rate is FX gain or loss, not margin. */
  fxTasaOficial: z
    .number()
    .int()
    .positive()
    .max(TASA_TOPE)
    .default(FX_OFICIAL_2026)
    .transform((n) => n as FxRate),

  // --- Caja ---
  /** Coin step for rounding CASH totals. 50 = C$0.50. Never applied to transfers. */
  cajaRedondeoCentavos: z.number().int().min(1).max(10_000).default(50),
  /** Hide the expected total until the cashier has entered their count. */
  cajaConteoCiego: z.boolean().default(true),
  /**
   * The float: córdobas left in the drawer overnight for change. The corte suggests leaving this
   * much and taking the rest out. Null: nothing (since 2026-10-14; it was whatever the shift opened
   * with).
   */
  cajaFondo: z.number().int().nonnegative().nullable().default(null),
  /**
   * A count of the drawer (opening, corte or handover) this far off what was expected, or
   * further, is asked again before it is taken, without saying by how much or which way: most
   * differences are a miscount. The second count stands, and the owner sees both. Dollars are
   * counted in whole bills, so any dollar off is asked again. Zero: every córdoba off is.
   */
  cajaReconteoDesde: z.number().int().nonnegative().default(2_000),
  /**
   * When each money account was last counted, by the account's code: general cash at a count of
   * the safe (`efectivo.arqueo`), the bank, the dollar account and the wallet at a statement
   * (`cuenta.conciliada`), each as the place in the log of the event that counted it. Written by
   * the books, never by hand (`config.cambiada` refuses it).
   *
   * General cash never counted: its count is where the safe starts, against capital, whatever
   * the phone says (one that had not heard of another's count would send a «first count»). And a
   * deposit, money for the owner, a supplier's payment or an expense is not voided once the
   * account it moved was counted after it: that count already found the difference and booked
   * it. A statement, only what is dated on or before the day it reads to (`estadosDeCuenta`).
   */
  cuentasContadas: z.record(z.string(), z.number().int()).default({}),
  /**
   * What a till's opening or corte found over (positive) or short (negative), in córdobas, once
   * the owner corrected it (`caja.apertura_corregida`, `caja.corte_corregido`), by the count's
   * event. A count not in it found what its own entries say. What a shortage explained may take
   * (`faltante.explicado`), and what «Control» puts on whoever had the drawer, are read here.
   * Written by the books, never by hand (`config.cambiada` refuses it).
   */
  conteosCorregidos: z.record(z.string(), z.number().int()).default({}),
  /**
   * The bank's, the dollar account's and the wallet's statements squared (`cuenta.conciliada`),
   * by the account's code, in the log's order: each one's place in the log, the day it reads to,
   * and, the dollar account's, the dollars it read. A statement found what was written down before
   * it and dated on or before its day; what is dated after, it never saw. One entered after
   * another and reading to as late a day or later found all the other did, which then drops out.
   * The last entered always stays: the dollar account holds what it read and the deposits since.
   * Written by the books, never by hand (`config.cambiada` refuses it).
   */
  estadosDeCuenta: z
    .record(
      z.string(),
      z.array(
        z.object({
          lugar: z.number().int(),
          fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          dolares: z.number().int().optional(),
        }),
      ),
    )
    .default({}),

  // --- Bancos ---
  /**
   * The bank accounts the owner opened or named (`bancos.ts`): the first in córdobas and the first
   * in dollars are always there, named «Banco» and «Banco en dólares» until the owner names them,
   * and up to four more of each are opened here. Closed ones stay on the books, offered nowhere.
   */
  cuentasBanco: cuentasBancoSchema.default([]),
  /**
   * Where the transfers a customer makes go: what confirming one, at the till or later, starts
   * with (the owner, 2026-10-10: «The default should be the nicaraguan bank account that the owner
   * can change later»). The account is written on each payment, so changing this changes only
   * what comes after.
   */
  cuentaTransferencias: cuentaBancoCordobasSchema.default('BANCOS'),
  /** Where card payments go: the bank the card terminal pays into (the owner, 2026-10-10). */
  cuentaTarjeta: cuentaBancoCordobasSchema.default('BANCOS'),

  // --- Precios ---
  /** Gross margin floor in basis points. Below this, a sale needs the owner's PIN. */
  precioMargenMinimoBp: z.number().int().min(0).max(10_000).default(1_000),
  /**
   * A venta rápida has no product behind it: no cost, no stock, no margin to check. The quick
   * sales on one ticket may come to this much on a cashier's word; past it, the owner's PIN.
   * Zero: every one takes the owner.
   */
  ventaRapidaMaxima: z.number().int().nonnegative().default(20_000),
  /**
   * A count that comes out this much short or more — a corte, a handover, cash received from a
   * till — goes on the owner's home screen, with whose it was. Zero: every shortage does.
   */
  faltanteAviso: z.number().int().nonnegative().default(10_000),

  // --- Seguridad ---
  /**
   * Minutes without a touch before the PIN pad comes back over the screen (`bloqueo.ts` in the
   * web app). There is no "never": a phone picked up in the morning must not still be signed in
   * as whoever closed the night before.
   */
  bloqueoMinutos: z.number().int().min(1).max(120).default(15),
  /**
   * Voiding a sale or an abono, and giving money back for a return, takes the owner's PIN rather
   * than the PIN of whoever is signed in — within the shift still open. Anything from before that
   * takes the owner's whatever this says (`usePinParaAnular` in the web app). Off by default: a
   * shop run by one family has no need of it, and one with a hired cashier turns it on.
   */
  anularConDueno: z.boolean().default(false),
  /**
   * Orders set aside what they take (the owner, 2026-10-08: «we need stock reservation by both
   * orders and the till»). On: an order is refused past what the shop holds less what the other
   * pending orders set aside, it is taken only with a connection, so the server is the one that
   * says yes, and the till asks the owner's PIN to sell what is set aside. Off by default, and
   * turned on as a change in the log, not as a default: the orders taken before it were never held
   * to it, and a replay must not refuse them now.
   */
  apartarPedidos: z.boolean().default(false),
  /**
   * The till sells only what is there (the owner, 2026-10-08: «I want the airtight version»). On: a
   * line past what is free (the shop's stock less what orders set aside) takes the owner's yes, and
   * a till that can reach the server sends each sale asking to be refused if it is not there
   * (`soloSiHay`), so the server decides between two tills selling the last of something before
   * either prints a receipt. With no connection the till sells on, saying so, and what it sells
   * then stands. Off by default; the sale itself carries the question, so a replay never refuses a
   * sale made before it was turned on.
   */
  venderSoloLoQueHay: z.boolean().default(false),

  // --- Impuestos ---
  /** Off: the business is not DGI-registered. Modelled so registration is a setting, not a migration. */
  ivaHabilitado: z.boolean().default(false),
  ivaTasaBp: z.number().int().min(0).max(10_000).default(1_500),

  // --- Reparto ---
  /**
   * Delivery vehicles. A truck is a stock location, so its id is literally the tail of
   * `camion:<id>` — which is why this is configuration and not a catalog: adding a truck must
   * not require a migration of every movement that ever referenced one.
   */
  camiones: z
    .array(
      z.object({
        id: z.string().uuid(),
        nombre: z.string().min(1).max(40),
        placa: z.string().max(20).default(''),
        activo: z.boolean().default(true),
      }),
    )
    .default([]),

  // --- Gastos fijos ---
  /**
   * What the shop pays every month or every few: rent, internet, the light (`gastosFijos.ts`).
   * A list, as the trucks are: a fixed expense moves no money until a month of it is recorded.
   */
  gastosFijos: z.array(gastoFijoSchema).max(100).default([]),

  // --- Vencimientos ---
  /** Days before expiry that a lot starts showing on the alert board. */
  vencimientoAlertaDias: z.number().int().min(1).max(365).default(30),

  // --- Análisis ---
  /**
   * The month's goals (the owner, 2026-10-13, «Análisis»): what each month's sales and gross profit
   * are measured against, in centavos, the same every month until changed. Null: none set.
   */
  metaVentasMes: z.number().int().positive().nullable().default(null),
  metaUtilidadMes: z.number().int().positive().nullable().default(null),
  /**
   * What each expense account should spend a month (the owner, 2026-10-13, «Análisis»), in centavos,
   * by the account's code: «6310» for the electricity. An account left out has none.
   */
  presupuestosGastos: z.record(z.string().regex(/^\d{4}$/), z.number().int().positive()).default({}),

  // --- Sincronización ---
  /** Warn the operator when a device clock has drifted this far from the server. */
  relojDerivaMaxMs: z.number().int().min(1_000).default(120_000),
  /** Warn when this many events are waiting to sync — a lost phone becomes a real loss. */
  syncPendientesAlerta: z.number().int().min(1).default(50),
  /**
   * Hours a device can go without reaching the server before the owner's home screen says so. A
   * phone that has stopped syncing holds sales and voids nobody has seen, and loses them if it
   * breaks; only the server can tell, since the phone itself is the one not calling.
   */
  equipoCalladoHoras: z.number().int().min(1).max(720).default(24),

  // --- Planilla (rates confirmed for 2026) ---
  /** INSS withheld from the employee: 7.00%. */
  inssLaboralBp: z.number().int().min(0).max(10_000).default(700),
  /** INSS paid by the employer, under 50 workers: 21.50%. (50+ workers is 22.50%.) */
  inssPatronalBp: z.number().int().min(0).max(10_000).default(2_150),
  /** INATEC, employer only: 2.00%. */
  inatecBp: z.number().int().min(0).max(10_000).default(200),
  irTramos: z.array(irBracketSchema).min(1).default(IR_TRAMOS_2026),
  /**
   * Vacations accrue: 2.5 days and a twelfth of the pay a month for whoever is on the benefits (Art.
   * 76 CT). The owner turned them off for the whole shop (2026-10-31: «i've just been told that
   * vacation pay accrual isn't a thing for this type of employment»): off, a run sets none aside and
   * earns no days, and a settlement pays only what was set aside before. Aguinaldo and severance go on.
   */
  vacacionesAcumulan: z.boolean().default(true),
});

export type Settings = z.infer<typeof settingsSchema>;

export const DEFAULT_SETTINGS: Settings = settingsSchema.parse({});

export type SettingKey = keyof Settings;
